import { z } from "zod";

// =============================================================
// Module local « x-garanties » (PROTECTO) : garanties antirouille 10 ans
// (cahier des charges §5.6). Une garantie est un document numéroté, figé au
// moment de l'émission (copies du client, du véhicule et des termes), qui
// remplace le document Canva rempli à la main. Types partagés API ↔ web.
// =============================================================

export type WarrantyStatus = "brouillon" | "envoyee" | "signee" | "expiree" | "annulee";

/** Durée de la garantie antirouille, en années. */
export const WARRANTY_YEARS = 10;

/** Longueur d'un numéro d'identification de véhicule (NIV). */
export const VIN_LENGTH = 17;

export interface Warranty {
  id: string;
  /** Numéro lisible, unique et séquentiel par année : « G-2026-0001 ». */
  number: string;
  clientId: string;
  vehicleId: string;
  appointmentId: string | null;
  status: WarrantyStatus;
  /** Date d'émission ; null tant que le brouillon n'a pas été émis. */
  issuedAt: number | string | null;
  /** Fin de couverture = émission + 10 ans. */
  expiresAt: number | string | null;
  /** NIV (17 caractères) — obligatoire pour émettre. */
  vin: string | null;
  mileage: number | null;
  // Copies figées au moment de l'émission : la fiche client peut changer
  // ensuite, le document, lui, reste ce qui a été remis.
  clientName: string;
  clientPhone: string | null;
  clientEmail: string | null;
  clientAddress: string | null;
  vehicleLabel: string;
  /** Service couvert (ex. « Antirouille permanent »). */
  serviceLabel: string | null;
  /** Termes de la garantie en markdown, pré-remplis depuis le modèle. */
  termsMd: string;
  notes: string | null;
  signedAt: number | string | null;
  signedBy: string | null;
  createdAt: number | string;
  updatedAt: number | string;
  createdByUserId: string | null;
}

export interface WarrantyTemplate {
  id: string;
  name: string;
  termsMd: string;
  /** Intervalle du rappel d'entretien, en mois (12 par défaut). */
  maintenanceMonths: number;
  updatedAt: number | string;
}

/** Valeurs proposées par l'API avant la création (rien n'est enregistré). */
export interface WarrantyPrefill {
  clientId: string;
  vehicleId: string;
  appointmentId: string | null;
  vin: string | null;
  mileage: number | null;
  clientName: string;
  clientPhone: string | null;
  clientEmail: string | null;
  clientAddress: string | null;
  vehicleLabel: string;
  serviceLabel: string | null;
  termsMd: string;
}

/** Chiffres du widget d'accueil. */
export interface WarrantyStats {
  /** Garanties émises (émission datée) dans le mois civil courant. */
  emittedThisMonth: number;
  /** Émises mais pas encore signées. */
  pendingSignature: number;
}

// ---- Libellés ----

export const WARRANTY_STATUS_LABELS: Record<WarrantyStatus, string> = {
  brouillon: "Brouillon",
  envoyee: "Émise",
  signee: "Signée",
  expiree: "Expirée",
  annulee: "Annulée",
};

export const WARRANTY_STATUS_ORDER: WarrantyStatus[] = [
  "brouillon",
  "envoyee",
  "signee",
  "expiree",
  "annulee",
];

/** Statuts dont les champs restent modifiables (tant que la garantie n'est pas signée). */
export const EDITABLE_STATUSES: WarrantyStatus[] = ["brouillon", "envoyee"];

export function isWarrantyEditable(status: WarrantyStatus): boolean {
  return EDITABLE_STATUSES.includes(status);
}

/** La garantie est remise au client pour signature (courriel avec le certificat PDF,
 * sinon texto avec le lien) : il faut au moins un moyen de le joindre. */
export function hasWarrantyContact(w: {
  clientPhone?: string | null;
  clientEmail?: string | null;
}): boolean {
  return Boolean(w.clientPhone?.trim() || w.clientEmail?.trim());
}

/** Un NIV valide : 17 caractères alphanumériques, sans I, O ni Q (norme ISO 3779). */
export function normalizeVin(value: string | null | undefined): string {
  return (value ?? "").trim().toUpperCase().replace(/\s+/g, "");
}

export function isValidVin(value: string | null | undefined): boolean {
  const vin = normalizeVin(value);
  return vin.length === VIN_LENGTH && /^[A-HJ-NPR-Z0-9]{17}$/.test(vin);
}

/** Fin de couverture : même jour, dix ans plus tard. */
export function expiresAtFrom(issuedAt: Date, years = WARRANTY_YEARS): Date {
  const out = new Date(issuedAt);
  out.setFullYear(out.getFullYear() + years);
  return out;
}

/** Prochain entretien : émission + `months` mois. */
export function maintenanceDueAt(issuedAt: Date, months: number): Date {
  const out = new Date(issuedAt);
  out.setMonth(out.getMonth() + months);
  return out;
}

/** « G-2026-0001 » : préfixe, année, séquence sur 4 chiffres. */
export function formatWarrantyNumber(year: number, sequence: number): string {
  return `G-${year}-${String(sequence).padStart(4, "0")}`;
}

// ---- Schémas ----

export const warrantyStatusSchema = z.enum([
  "brouillon",
  "envoyee",
  "signee",
  "expiree",
  "annulee",
]);

const optionalText = z.string().trim().max(500).nullable().optional();

/** Date acceptée en entrée : ISO 8601 (avec décalage) ou « AAAA-MM-JJ ». */
export const dateInput = z
  .string()
  .trim()
  .refine((s) => !Number.isNaN(Date.parse(s)), { message: "date invalide" });

/** Champs modifiables d'une garantie (brouillon ou émise). */
export const warrantyFieldsSchema = z.object({
  vin: z.string().trim().max(VIN_LENGTH).nullable().optional(),
  mileage: z.number().int().min(0).nullable().optional(),
  clientName: z.string().trim().min(1).max(160).optional(),
  clientPhone: optionalText,
  clientEmail: z.union([z.string().trim().email(), z.literal(""), z.null()]).optional(),
  clientAddress: optionalText,
  vehicleLabel: z.string().trim().min(1).max(160).optional(),
  serviceLabel: z.string().trim().max(200).nullable().optional(),
  termsMd: z.string().max(50_000).optional(),
  notes: z.string().max(5000).nullable().optional(),
  issuedAt: dateInput.nullable().optional(),
});
export type WarrantyFieldsInput = z.infer<typeof warrantyFieldsSchema>;

/**
 * Création : depuis un rendez-vous (`appointmentId`) OU un couple
 * client + véhicule. Les autres champs surchargent le pré-remplissage.
 */
export const createWarrantySchema = warrantyFieldsSchema
  .extend({
    appointmentId: z.string().min(1).optional(),
    clientId: z.string().min(1).optional(),
    vehicleId: z.string().min(1).optional(),
  })
  .refine((d) => Boolean(d.appointmentId) || Boolean(d.clientId && d.vehicleId), {
    message: "appointmentId ou clientId + vehicleId requis",
    path: ["appointmentId"],
  });
export type CreateWarrantyInput = z.infer<typeof createWarrantySchema>;

export const updateWarrantySchema = warrantyFieldsSchema;

/** Demande de signature : `renew` refait le lien (l'ancien cesse de fonctionner) et le
 * renvoie par le canal actuel du client, tant que la garantie n'est pas signée. */
export const requestSignatureSchema = z.object({ renew: z.boolean().optional() });
export type RequestSignatureInput = z.infer<typeof requestSignatureSchema>;

export const signWarrantySchema = z.object({
  signedBy: z.string().trim().min(1).max(160),
  signedAt: dateInput.optional(),
});
export type SignWarrantyInput = z.infer<typeof signWarrantySchema>;

export const updateTemplateSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  termsMd: z.string().max(50_000).optional(),
  maintenanceMonths: z.number().int().min(1).max(120).optional(),
});
export type UpdateTemplateInput = z.infer<typeof updateTemplateSchema>;
